/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/components/TraceDashboard.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { HugeiconsIcon } from "@hugeicons/react";
import { Refresh01Icon, Hospital01Icon, UserSearch01Icon, Notification01Icon, Settings01Icon, Cancel01Icon, Database01Icon, Sun03Icon, Moon02Icon, MapsIcon, UserMultipleIcon, Image01Icon, Building03Icon, Share08Icon } from "@hugeicons/core-free-icons";
import { Button } from "./ui/button.js";
import { Skeleton } from "./ui/skeleton.js";
import { AlertsPanel } from "./shared/ActivityPanel.js";
import { InvestigationPanel } from "./shared/InvestigationPanel.js";
import { CorpusTotals, is_corpus_snapshot, fmt_count } from "./shared/Scale.js";
import { StoryBar, story_tab } from "./shared/StoryBar.js";
import { AlertBanner } from "./shared/AlertBanner.js";
import { MapTab } from "../features/map/MapTab.js";
import { PeopleTab } from "../features/people/PeopleTab.js";
import { MediaTab } from "../features/media/MediaTab.js";
import { OrganizationsTab } from "../features/organizations/OrganizationsTab.js";
import { GraphTab } from "../features/graph/GraphTab.js";
import { __jacCallFunction } from "@jac/runtime";
import { useState } from "@jac/runtime";
import { useEffect } from "@jac/runtime";
import { _jac } from "@jac/prelude";
async function get_dashboard(incident_id) {
  return await __jacCallFunction("get_dashboard", {"incident_id": incident_id});
}
async function reset_demo() {
  return await __jacCallFunction("reset_demo", {});
}
async function add_hospital_update() {
  return await __jacCallFunction("add_hospital_update", {});
}
async function run_watch() {
  return await __jacCallFunction("run_watch", {});
}
async function run_resolve() {
  return await __jacCallFunction("run_resolve", {});
}
async function list_incidents() {
  return await __jacCallFunction("list_incidents", {});
}
async function load_corpus() {
  return await __jacCallFunction("load_corpus", {});
}
let USE_DARK_THEME = true;
let DASHBOARD_TABS = ["Map", "People", "Media", "Sources", "Graph"];
let TAB_ICONS = [MapsIcon, UserMultipleIcon, Image01Icon, Building03Icon, Share08Icon];
function apply_theme(dark) {
  if (dark) {
    _jac.poly.call(document.documentElement.classList, "add", "dark");
  } else {
    _jac.poly.call(document.documentElement.classList, "remove", "dark");
  }
}
function saved_theme_is_dark(default_dark) {
  let saved;
  try {
    saved = localStorage.getItem("trace-theme");
    if ((saved === "light")) {
      return false;
    }
    if ((saved === "dark")) {
      return true;
    }
  } catch (__jac_e) {
    if (_jac.exc.matches(__jac_e, "Exception")) {
      return default_dark;
    } else {
      throw __jac_e;
    }
  }
  return default_dark;
}
function scroll_to_later(element_id) {
  setTimeout(() => {
    let target = document.getElementById(element_id);
    if (target) {
      target.scrollIntoView({"behavior": "smooth", "block": "start"});
    }
  }, 150);
}
function TraceDashboard() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [activeTab, setActiveTab] = useState("Map");
  const [showDemo, setShowDemo] = useState(false);
  const [mapFocusId, setMapFocusId] = useState("");
  const [mapFocusRequest, setMapFocusRequest] = useState(0);
  const [incidents, setIncidents] = useState([]);
  const [incidentId, setIncidentId] = useState("");
  const [darkMode, setDarkMode] = useState(USE_DARK_THEME);
  const [storyStep, setStoryStep] = useState(1);
  const [reviewRequest, setReviewRequest] = useState(0);
  const [investigateRequest, setInvestigateRequest] = useState(0);
  const [bannerId, setBannerId] = useState("");
  useEffect(() => {
    let startDark = saved_theme_is_dark(USE_DARK_THEME);
    apply_theme(startDark);
    setDarkMode(startDark);
    loadStartup();
  }, []);
  async function loadDashboard() {
    await loadIncidentSnapshot(incidentId);
  }
  async function loadStartup() {
    await loadDashboard();
    await loadIncidents();
  }
  async function loadIncidentSnapshot(targetId) {
    try {
      setSnapshot(await __jacCallFunction("get_dashboard", {"incident_id": targetId}));
      setError("");
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("Could not load the Trace graph: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setLoading(false);
  }
  async function loadIncidents() {
    try {
      setIncidents(await __jacCallFunction("list_incidents", {}));
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setIncidents([]);
      } else {
        throw __jac_e;
      }
    }
  }
  async function handleSelectIncident(targetId) {
    if ((targetId === incidentId)) {
      return;
    }
    setBusy("switch");
    setNotice("");
    setIncidentId(targetId);
    await loadIncidentSnapshot(targetId);
    setBusy("");
  }
  async function handleLoadCorpus() {
    setBusy("corpus");
    try {
      await load_corpus();
      await loadIncidents();
      setNotice("Exercise corpus loaded. Choose it in the incident selector.");
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("Loading the exercise corpus failed: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setBusy("");
  }
  async function handleReset() {
    setBusy("reset");
    try {
      await __jacCallFunction("reset_demo", {});
      setNotice("Demo reset. Maya Gurung is back to one police report saying she is missing.");
      setBannerId("");
      setStoryStep(1);
      setActiveTab("Map");
      await loadDashboard();
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("Reset failed: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setBusy("");
  }
  async function handleHospitalUpdate() {
    let created;
    let fresh;
    setBusy("hospital");
    try {
      created = await __jacCallFunction("add_hospital_update", {});
      fresh = await __jacCallFunction("get_dashboard", {"incident_id": incidentId});
      setSnapshot(fresh);
      setNotice((("Hospital report added. Trace sent " + String(created)) + " alert(s) to people following Maya."));
      if (((created > 0) && (fresh.alerts.length > 0))) {
        setBannerId((fresh.alerts[0].id || fresh.alerts[0].created_at));
      }
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("Hospital update failed: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setBusy("");
  }
  async function handleRunWatch() {
    let created;
    setBusy("watch");
    try {
      created = await __jacCallFunction("run_watch", {});
      setNotice((("Checked for new reports: " + String(created)) + " new alert(s)."));
      await loadDashboard();
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("WatchWalker failed: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setBusy("");
  }
  async function handleRunResolve() {
    let found;
    setBusy("resolve");
    try {
      found = await __jacCallFunction("run_resolve", {});
      setNotice((("Found " + String(found.length)) + " possible match(es). Nothing was merged."));
      await loadDashboard();
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setError(("ResolveWalker failed: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setBusy("");
  }
  function handleTabKey(event, index) {
    let nextIndex;
    let nextTab;
    let target;
    nextIndex = index;
    if ((event.key === "ArrowRight")) {
      nextIndex = _jac.int.mod((index + 1), DASHBOARD_TABS.length);
    } else if ((event.key === "ArrowLeft")) {
      nextIndex = _jac.int.mod(((index + DASHBOARD_TABS.length) - 1), DASHBOARD_TABS.length);
    } else if ((event.key === "Home")) {
      nextIndex = 0;
    } else if ((event.key === "End")) {
      nextIndex = (DASHBOARD_TABS.length - 1);
    } else {
      return;
    }
    event.preventDefault();
    nextTab = DASHBOARD_TABS.at(nextIndex);
    setActiveTab(nextTab);
    target = document.getElementById(("tab-" + nextTab));
    if (target) {
      target.focus();
    }
  }
  function handleShowOnMap(claimId) {
    let current;
    let locations;
    let places;
    let target;
    current = snapshot;
    if ((current == null)) {
      return;
    }
    locations = current.locations;
    places = locations.filter(p => p.evidence.map(e => e.claim_id).includes(claimId)).map(p => p);
    if (!_jac.builtin.bool(places)) {
      return;
    }
    setMapFocusId(places[0].id);
    setMapFocusRequest((mapFocusRequest + 1));
    setActiveTab("Map");
    target = document.getElementById("tab-Map");
    if (target) {
      target.focus();
    }
  }
  function handleShowPersonOnMap(personId) {
    let current;
    let places;
    current = snapshot;
    if ((current == null)) {
      return;
    }
    places = current.locations.filter(p => p.evidence.map(e => e.person_id).includes(personId)).map(p => p);
    if (!_jac.builtin.bool(places)) {
      return;
    }
    setMapFocusId(places[0].id);
    setMapFocusRequest((mapFocusRequest + 1));
    setActiveTab("Map");
  }
  function handleToggleTheme() {
    let nextDark;
    nextDark = !darkMode;
    apply_theme(nextDark);
    setDarkMode(nextDark);
    try {
      localStorage.setItem("trace-theme", (nextDark ? "dark" : "light"));
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        return;
      } else {
        throw __jac_e;
      }
    }
  }
  function handleStoryStep(step) {
    let tab;
    let current;
    let places;
    tab = story_tab(step);
    setStoryStep(step);
    setActiveTab(tab);
    current = snapshot;
    if (((tab === "Map") && current)) {
      places = current.locations.filter(p => (p.evidence.length > 0)).map(p => p);
      if (_jac.builtin.bool(places)) {
        setMapFocusId(places[0].id);
        setMapFocusRequest((mapFocusRequest + 1));
      }
    }
  }
  async function handleStoryAction(step) {
    if ((step === 1)) {
      setActiveTab("People");
      scroll_to_later("panel-People");
    } else if ((step === 2)) {
      setActiveTab("People");
      setReviewRequest((reviewRequest + 1));
      scroll_to_later("identity-review");
    } else if ((step === 3)) {
      await handleHospitalUpdate();
    } else if ((step === 4)) {
      setActiveTab("People");
      setInvestigateRequest((investigateRequest + 1));
      scroll_to_later("report-investigation");
    }
  }
  if ((loading && (snapshot == null))) {
    return __jacJsx("div", {"className": "min-h-svh bg-background p-6", "role": "status", "aria-label": "Loading incident"}, [__jacJsx("div", {"className": "flex flex-col gap-4"}, [__jacJsx(Skeleton, {"className": "h-12 w-72"}, []), __jacJsx(Skeleton, {"className": "h-[70svh] w-full"}, [])])]);
  }
  let snap = snapshot;
  let hospital_added = (snap ? snap.hospital_update_added : false);
  let corpus_view = is_corpus_snapshot(snap);
  let has_corpus = (incidents.filter(i => (i.kind === "corpus")).map(i => i).length > 0);
  let banner_alerts = ((snap && !corpus_view) ? snap.alerts.filter(a => ((bannerId !== "") && ((a.id || a.created_at) === bannerId))).map(a => a) : []);
  return __jacJsx("div", {"className": "flex min-h-svh flex-col bg-background text-foreground"}, [__jacJsx("header", {"className": "border-b border-border"}, [__jacJsx("div", {"className": "flex flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-6"}, [__jacJsx("div", {"className": "flex min-w-0 items-center gap-4"}, [__jacJsx("div", {"className": "flex flex-col"}, [__jacJsx("h1", {"className": "font-display text-3xl leading-none tracking-tight md:text-4xl"}, ["Trace"]), __jacJsx("span", {"className": "mt-1 text-xs text-muted-foreground"}, ["One incident. Every trace."])]), __jacJsx("span", {"className": "hidden h-10 w-px bg-border sm:block", "aria-hidden": "true"}, []), __jacJsx("div", {"className": "flex min-w-0 flex-col"}, [__jacJsx("span", {"className": "text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"}, ["Current incident"]), __jacJsx("span", {"className": "break-words font-display text-lg leading-tight md:text-xl"}, [(snap ? snap.incident_name : "Unavailable")]), __jacJsx("span", {"className": "text-xs text-muted-foreground"}, [(snap ? snap.incident_region : "")])])]), __jacJsx("div", {"className": "flex flex-wrap items-center gap-2"}, [((incidents.length > 1) && __jacJsx("div", {"role": "group", "aria-label": "Choose an incident", "className": "flex max-w-full flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1"}, [(() => {
    let __jac_view_kids = [false];
    __jac_view_kids[0] = (() => {
      let __jac_view_kids_1 = [];
      for (const opt of incidents) {
        __jac_view_kids_1.push(__jacJsx("button", {"key": opt.id, "type": "button", "aria-pressed": (((opt.kind === "demo") && (incidentId === "")) || ((opt.kind !== "demo") && (opt.id === incidentId))), "disabled": (busy !== ""), "onClick": () => handleSelectIncident(((opt.kind === "demo") ? "" : opt.id)), "className": ("min-h-9 rounded px-3 py-1.5 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 " + (((((opt.kind === "demo") && (incidentId === "")) || ((opt.kind !== "demo") && (opt.id === incidentId))) ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground")))}, [((opt.kind === "demo") ? "Maya story (demo)" : (((opt.name + " - ") + fmt_count(opt.record_count)) + " records"))]));
      }
      return __jac_view_kids_1;
    })();
    return __jacJsx(null, {}, __jac_view_kids);
  })(), ((busy === "switch") && __jacJsx("span", {"role": "status", "className": "px-2 text-xs text-muted-foreground"}, ["Loading incident..."]))])), __jacJsx(Button, {"variant": "outline", "size": "icon-lg", "onClick": handleToggleTheme, "aria-label": (darkMode ? "Switch to light theme" : "Switch to dark theme"), "title": (darkMode ? "Switch to light theme" : "Switch to dark theme")}, [__jacJsx(HugeiconsIcon, {"icon": (darkMode ? Sun03Icon : Moon02Icon), "strokeWidth": 2}, [])]), __jacJsx(Button, {"variant": "ghost", "size": "sm", "onClick": () => {
    setShowDemo(!showDemo);
  }}, [__jacJsx(HugeiconsIcon, {"icon": (showDemo ? Cancel01Icon : Settings01Icon), "strokeWidth": 2}, []), (showDemo ? "Hide demo controls" : "Demo controls")])])]), (showDemo && __jacJsx("div", {"className": "flex flex-wrap items-center gap-2 border-t border-border bg-muted/40 pt-2.5 pb-2.5 pl-4 pr-4 md:pl-6 md:pr-6"}, [__jacJsx("span", {"className": "mr-2 text-xs text-muted-foreground"}, ["Presenter tools:"]), (!has_corpus && __jacJsx(Button, {"variant": "outline", "size": "sm", "disabled": (busy !== ""), "onClick": handleLoadCorpus}, [__jacJsx(HugeiconsIcon, {"icon": Database01Icon, "strokeWidth": 2}, []), ((busy === "corpus") ? "Loading corpus, this can take a minute..." : "Load exercise corpus")])), (corpus_view && __jacJsx("span", {"className": "text-xs text-muted-foreground"}, ["The hospital report, update check, match search and reset act on the Maya story. Switch to it to use them."])), (!corpus_view && __jacJsx(Button, {"size": "sm", "disabled": ((busy !== "") || hospital_added), "onClick": handleHospitalUpdate}, [__jacJsx(HugeiconsIcon, {"icon": Hospital01Icon, "strokeWidth": 2}, []), ((busy === "hospital") ? "Adding..." : (hospital_added ? "Hospital report added" : "Simulate hospital report"))])), (!corpus_view && __jacJsx(Button, {"variant": "outline", "size": "sm", "disabled": (busy !== ""), "onClick": handleRunWatch}, [__jacJsx(HugeiconsIcon, {"icon": Notification01Icon, "strokeWidth": 2}, []), "Check for updates"])), (!corpus_view && __jacJsx(Button, {"variant": "outline", "size": "sm", "disabled": (busy !== ""), "onClick": handleRunResolve}, [__jacJsx(HugeiconsIcon, {"icon": UserSearch01Icon, "strokeWidth": 2}, []), "Look for possible matches"])), (!corpus_view && __jacJsx(Button, {"variant": "outline", "size": "sm", "disabled": (busy !== ""), "onClick": handleReset}, [__jacJsx(HugeiconsIcon, {"icon": Refresh01Icon, "strokeWidth": 2}, []), ((busy !== "reset") ? "Reset demo" : "Resetting...")]))]))]), ((snap && !corpus_view) && __jacJsx(StoryBar, {"step": storyStep, "hospitalAdded": hospital_added, "busy": busy, "onStep": handleStoryStep, "onAction": handleStoryAction, "onShowGraph": () => {
    setActiveTab("Graph");
  }, "onReset": handleReset}, [])), (() => {
    let __jac_view_kids_2 = [false];
    __jac_view_kids_2[0] = (() => {
      let __jac_view_kids_3 = [];
      for (const a of banner_alerts) {
        __jac_view_kids_3.push(__jacJsx(AlertBanner, {"key": (a.id || a.created_at), "alert": a, "people": (snap ? snap.people : []), "onDismiss": () => {
          setBannerId("");
        }, "onShowOnMap": handleShowPersonOnMap}, []));
      }
      return __jac_view_kids_3;
    })();
    return __jacJsx(null, {}, __jac_view_kids_2);
  })(), __jacJsx("div", {"className": "flex flex-col gap-4 border-b border-border px-4 pt-4 md:px-6"}, [(corpus_view && __jacJsx(CorpusTotals, {"snapshot": snap}, [])), __jacJsx("div", {"role": "tablist", "aria-label": "Incident views", "className": "flex gap-1 overflow-x-auto"}, [(() => {
    let __jac_view_kids_4 = [false];
    __jac_view_kids_4[0] = (() => {
      let __jac_view_kids_5 = [];
      for (const i of _jac.builtin.range(DASHBOARD_TABS.length)) {
        __jac_view_kids_5.push(__jacJsx("button", {"key": DASHBOARD_TABS.at(i), "id": ("tab-" + DASHBOARD_TABS.at(i)), "type": "button", "role": "tab", "aria-selected": (activeTab === DASHBOARD_TABS.at(i)), "aria-controls": ("panel-" + DASHBOARD_TABS.at(i)), "tabIndex": ((activeTab === DASHBOARD_TABS.at(i)) ? 0 : (-1)), "onClick": () => {
          setActiveTab(DASHBOARD_TABS.at(i));
        }, "onKeyDown": event => handleTabKey(event, i), "className": ("inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-base font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring " + (((activeTab === DASHBOARD_TABS.at(i)) ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:bg-accent hover:text-foreground")))}, [__jacJsx(HugeiconsIcon, {"icon": TAB_ICONS.at(i), "strokeWidth": 2, "className": "size-4", "aria-hidden": "true"}, []), DASHBOARD_TABS.at(i)]));
      }
      return __jac_view_kids_5;
    })();
    return __jacJsx(null, {}, __jac_view_kids_4);
  })()])]), (error && __jacJsx("div", {"role": "alert", "className": "mx-4 mt-4 flex items-center justify-between gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive md:mx-6"}, [__jacJsx("span", {}, [error]), __jacJsx(Button, {"variant": "outline", "size": "sm", "onClick": loadDashboard}, ["Try again"])])), ((notice && (banner_alerts.length === 0)) && __jacJsx("p", {"role": "status", "className": "mx-4 mt-4 rounded-md border-l-4 border-primary bg-primary/5 px-3 py-2 text-sm md:mx-6"}, [notice])), (snap && __jacJsx("div", {"className": "grid flex-1 xl:grid-cols-[minmax(0,1fr)_320px]"}, [__jacJsx("main", {"className": "min-w-0 p-4 md:p-6"}, [__jacJsx("div", {"id": "panel-Map", "role": "tabpanel", "aria-labelledby": "tab-Map", "tabIndex": 0, "hidden": (activeTab !== "Map")}, [__jacJsx(MapTab, {"key": ("map-" + ((darkMode ? "dark" : "light"))), "snapshot": snap, "onRefresh": loadDashboard, "active": (activeTab === "Map"), "focusLocationId": mapFocusId, "focusRequest": mapFocusRequest}, [])]), __jacJsx("div", {"id": "panel-People", "role": "tabpanel", "aria-labelledby": "tab-People", "tabIndex": 0, "hidden": (activeTab !== "People")}, [__jacJsx(PeopleTab, {"snapshot": snap, "onRefresh": loadDashboard, "onShowOnMap": handleShowOnMap, "reviewRequest": reviewRequest}, []), (!corpus_view && __jacJsx(InvestigationPanel, {"snapshot": snap, "onRefresh": loadDashboard, "focusRequest": investigateRequest}, []))]), __jacJsx("div", {"id": "panel-Media", "role": "tabpanel", "aria-labelledby": "tab-Media", "tabIndex": 0, "hidden": (activeTab !== "Media")}, [__jacJsx(MediaTab, {"snapshot": snap, "onRefresh": loadDashboard}, [])]), __jacJsx("div", {"id": "panel-Sources", "role": "tabpanel", "aria-labelledby": "tab-Sources", "tabIndex": 0, "hidden": (activeTab !== "Sources")}, [__jacJsx(OrganizationsTab, {"snapshot": snap, "onRefresh": loadDashboard}, [])]), __jacJsx("div", {"id": "panel-Graph", "role": "tabpanel", "aria-labelledby": "tab-Graph", "tabIndex": 0, "hidden": (activeTab !== "Graph")}, [__jacJsx(GraphTab, {"snapshot": snap, "onRefresh": loadDashboard}, [])])]), __jacJsx("aside", {"aria-label": "Incident notifications and activity", "className": "min-w-0 border-t border-border bg-card p-4 md:p-6 xl:border-l xl:border-t-0"}, [__jacJsx(AlertsPanel, {"alerts": snap.alerts, "activity": snap.activity}, [])])])), __jacJsx("footer", {"className": "mt-auto flex flex-wrap justify-between gap-2 border-t border-border px-4 py-3 text-xs text-muted-foreground md:px-6"}, [__jacJsx("span", {}, ["Demo with made-up people. Reports can be wrong: confirm with the source."]), __jacJsx("span", {}, ["Built with Jac for JacHacks UMich"])])]);
}
/*jac:refresh-boundary*/;
export {TraceDashboard};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:get_dashboard": {"is_writer": true, "touches": ["*"]}, "func:reset_demo": {"is_writer": false, "touches": ["*"]}, "func:add_hospital_update": {"is_writer": true, "touches": ["*"]}, "func:run_watch": {"is_writer": true, "touches": ["*"]}, "func:run_resolve": {"is_writer": true, "touches": ["*"]}, "func:list_incidents": {"is_writer": true, "touches": ["*"]}}); };
//# sourceMappingURL=TraceDashboard.js.map
